import type { TrafficCalculatorSliderRange } from "@/components/services/seo/seo-revenue-projection";

/** Slider ranges for "Vad kan annonsering ge er?" (defaults are labelled examples). */
export const AD_CALCULATOR_RANGES = {
  budget: { min: 5_000, max: 500_000, step: 5_000, dragStep: 1_000, fallback: 30_000 },
  cpc: { min: 1, max: 200, step: 1, fallback: 15 },
  conversion: { min: 1, max: 20, step: 1, fallback: 3 },
  close: { min: 5, max: 100, step: 5, fallback: 30 },
  value: { min: 200, max: 500_000, step: 100, dragStep: 100, fallback: 8_000 },
} satisfies Record<string, TrafficCalculatorSliderRange>;

/** Budget multiples shown in the chart (same cost per click assumed). */
export const AD_BUDGET_STEPS = [0.5, 1, 1.5, 2] as const;

export type AdInputs = {
  /** Ad budget per month, SEK. */
  budget: number;
  /** Cost per click, SEK. */
  cpc: number;
  /** Share of visitors who buy or enquire, percent. */
  conversionPercent: number;
  /** Share of those who become customers, percent. */
  closePercent: number;
  /** Average order value, SEK. */
  value: number;
};

export type AdResult = {
  visits: number;
  leads: number;
  customers: number;
  revenue: number;
  /** Revenue per advertising krona (ROAS). */
  returnPerKrona: number;
  /** Ad budget per new customer; 0 when there are no customers. */
  costPerCustomer: number;
  /** Revenue per month at each of AD_BUDGET_STEPS. */
  revenueByBudget: number[];
};

const clampPercent = (value: number) => Math.min(100, Math.max(0, value)) / 100;

function revenueFor(budget: number, inputs: AdInputs) {
  const cpc = Math.max(0.01, inputs.cpc);
  const visits = Math.max(0, budget) / cpc;
  const leads = visits * clampPercent(inputs.conversionPercent);
  const customers = leads * clampPercent(inputs.closePercent);
  return { visits, leads, customers, revenue: customers * Math.max(0, inputs.value) };
}

/**
 * Budget → visitors → purchases/enquiries → customers → revenue, plus the
 * return per krona and the same figures at a few budget levels. Linear on
 * purpose (the page notes that click prices usually rise with budget).
 */
export function computeAdResult(inputs: AdInputs): AdResult {
  const budget = Math.max(0, inputs.budget);
  const base = revenueFor(budget, inputs);
  return {
    ...base,
    returnPerKrona: budget > 0 ? base.revenue / budget : 0,
    costPerCustomer: base.customers > 0 ? budget / base.customers : 0,
    revenueByBudget: AD_BUDGET_STEPS.map((step) => revenueFor(budget * step, inputs).revenue),
  };
}
